import type { ReactNode } from "react";
import { Zap } from "lucide-react";
import type {
    CockpitButton,
    CockpitQuickActionsCard,
    CockpitStatusCard,
    FieldDef,
    FormButtonElement,
    FormCockpitComponent,
    FormCockpitElement,
} from "../core";
import { CockpitAssistant } from "./CockpitAssistant";
import { CockpitChecklist } from "./CockpitChecklist";
import { CockpitStatus } from "./CockpitStatus";
import { CockpitSuggestions } from "./CockpitSuggestions";
import { CockpitTasks } from "./CockpitTasks";
import { DecisionCard } from "./DecisionCard";

// FormCockpit — the record cockpit side pane (form-view revamp Enh 14).
//
// Each component is dispatched through the cockpit-component REGISTRY. Built-ins
// today are `status` + `quick-actions`; the engine-backed cards (decision /
// assistant / suggestions / tasks / checklist) register their renderers as their
// engines land (Stage 3+). A component with no registered renderer silently
// no-renders — graceful degradation, exactly as the DSL contract specifies.

type Rec = Record<string, unknown> | null | undefined;

interface CockpitCtx {
    record: Rec;
    modelKey?: string;
    /** Saved record uuid; null on a new/unsaved record. */
    recordRef?: string | null;
    fieldDefs?: FieldDef[];
    actionSlug?: string;
    /** Action buttons declared in <header> — surfaced in the Status card. */
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
}

type CockpitRenderer = (component: FormCockpitComponent, ctx: CockpitCtx) => ReactNode;

const registry = new Map<string, CockpitRenderer>();

/** Register a cockpit card renderer by component type (or `card:<key>` for
 *  `<card key="…"/>`). Lets future modules plug cards in without kernel changes. */
export function registerCockpitCard(type: string, renderer: CockpitRenderer): void {
    registry.set(type, renderer);
}

function lookup(component: FormCockpitComponent): CockpitRenderer | undefined {
    if (component.type === "card") return registry.get(`card:${component.key ?? ""}`);
    return registry.get(component.type);
}

// ── button wiring ───────────────────────────────────────────────────────────
function runButton(btn: CockpitButton, ctx: CockpitCtx): void {
    if (!ctx.onRunButton || !btn.command) return;
    ctx.onRunButton({
        type: "button",
        name: btn.command,
        command: btn.command,
        label: btn.label ?? null,
        icon: btn.icon ?? null,
        confirm: btn.confirm ?? null,
    } as FormButtonElement);
}

function CockpitButtons({ buttons, ctx, variant }: { buttons: CockpitButton[]; ctx: CockpitCtx; variant: string }): ReactNode {
    if (buttons.length === 0) return null;
    return (
        <div className={`cockpit-actions cockpit-actions--${variant}`}>
            {buttons.map((b, i) => (
                <button
                    key={`${b.command ?? b.label ?? "act"}-${i}`}
                    type="button"
                    className="cockpit-action-btn"
                    onClick={() => runButton(b, ctx)}
                >
                    {b.label ?? b.command ?? "Action"}
                </button>
            ))}
        </div>
    );
}

// ── built-in cards ──────────────────────────────────────────────────────────
function QuickActionsCard({ card, ctx }: { card: CockpitQuickActionsCard; ctx: CockpitCtx }): ReactNode {
    if (card.actions.length === 0) return null;
    return (
        <div className="cockpit-card cockpit-card--quick">
            <div className="cockpit-card__head"><Zap size={13} /> Quick actions</div>
            <div className="cockpit-card__body">
                <CockpitButtons buttons={card.actions} ctx={ctx} variant="grid" />
            </div>
        </div>
    );
}

registerCockpitCard("status", (c, ctx) => {
    const card = c as CockpitStatusCard;
    return (
        <CockpitStatus
            field={card.field}
            showAge={card.showAge}
            buttons={card.buttons}
            statusbarVisible={card.statusbarVisible ?? null}
            record={ctx.record}
            modelKey={ctx.modelKey}
            recordRef={ctx.recordRef ?? null}
            fieldDefs={ctx.fieldDefs}
            actionSlug={ctx.actionSlug}
            headerButtons={ctx.headerButtons}
            onRunButton={ctx.onRunButton}
            onRunTransition={ctx.onRunTransition}
        />
    );
});
registerCockpitCard("quick-actions", (c, ctx) => <QuickActionsCard card={c as CockpitQuickActionsCard} ctx={ctx} />);
// <tasks> — backed by the activity.log engine (foundation.communication). Renders
// only on a saved record (needs related_model/related_id); skips otherwise.
registerCockpitCard("tasks", (_c, ctx) =>
    ctx.modelKey ? <CockpitTasks modelKey={ctx.modelKey} recordRef={ctx.recordRef ?? null} /> : null,
);
// <checklist> — backed by workflow stage requirements (Enhancement 02). Omits
// when the model declares no workflow requirements on the current stage.
registerCockpitCard("checklist", (_c, ctx) =>
    ctx.modelKey ? <CockpitChecklist modelKey={ctx.modelKey} recordRef={ctx.recordRef ?? null} /> : null,
);
// <assistant> — record-scoped AI summary + nudges + the cockpit Ask entry point
// (Stage 3D, backed by foundation.ai_insights narration). Omits when not
// narratable / on a new record.
registerCockpitCard("assistant", (_c, ctx) =>
    ctx.modelKey ? <CockpitAssistant modelKey={ctx.modelKey} recordRef={ctx.recordRef ?? null} /> : null,
);
// <suggestions> — "Suggested next": record-scoped recommended actions from
// foundation.ai_insights (consumed via insights.evaluate / runAction), colour-
// coded by severity + one-click confirm-to-act. Omits when no actions / no
// decision profile (or the engine isn't present).
registerCockpitCard("suggestions", (_c, ctx) =>
    ctx.modelKey ? <CockpitSuggestions modelKey={ctx.modelKey} recordRef={ctx.recordRef ?? null} /> : null,
);
// <decision> — deterministic AI-Insights decision (foundation.ai_insights, main's
// canonical engine): score ring · severity chip · ranked factors · recommended
// actions (confirm-to-act) · scoped chat. Reuses main's DecisionCard as a cockpit
// card (side-pane placement per the revamp mockup). Omits on a new record / when
// the record has no decision profile.
registerCockpitCard("decision", (c, ctx) =>
    ctx.modelKey ? (
        <DecisionCard
            modelKey={ctx.modelKey}
            recordRef={ctx.recordRef ?? null}
            title={(c as { title?: string | null }).title ?? null}
        />
    ) : null,
);

// ── pane ────────────────────────────────────────────────────────────────────
interface FormCockpitProps {
    cockpit: FormCockpitElement;
    record: Rec;
    modelKey?: string;
    recordRef?: string | null;
    fieldDefs?: FieldDef[];
    actionSlug?: string;
    /** Action buttons declared in <header> — surfaced in the Status card. */
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
}

export function FormCockpit({ cockpit, record, modelKey, recordRef, fieldDefs, actionSlug, headerButtons, onRunButton, onRunTransition }: FormCockpitProps): ReactNode {
    const ctx: CockpitCtx = { record, modelKey, recordRef, fieldDefs, actionSlug, headerButtons, onRunButton, onRunTransition };
    const rendered = cockpit.components
        .map((component, i) => {
            const renderer = lookup(component);
            if (!renderer) return null; // graceful degradation — capability not built/registered
            const node = renderer(component, ctx);
            return node ? <div key={`${component.type}-${i}`}>{node}</div> : null;
        })
        .filter(Boolean);

    if (rendered.length === 0) return null;
    return <aside className="form-cockpit">{rendered}</aside>;
}
